
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from "sonner";
import { Product } from '@/types';

// Type for favorite items
export interface FavoriteItem {
  productId: string;
  product: Product;
  dateAdded: string;
}

// Type for the context
interface FavoritesContextType {
  favorites: FavoriteItem[];
  isFavorite: (productId: string) => boolean;
  addToFavorites: (item: FavoriteItem) => void;
  removeFromFavorites: (productId: string) => void;
  clearFavorites: () => void;
}

// Create the context with default values
const FavoritesContext = createContext<FavoritesContextType>({
  favorites: [],
  isFavorite: () => false,
  addToFavorites: () => {},
  removeFromFavorites: () => {},
  clearFavorites: () => {},
});

// Context provider component
export const FavoritesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);
  const { user } = useAuth();

  // Load favorites from database when user changes
  useEffect(() => {
    if (user) {
      fetchFavorites();
    } else {
      setFavorites([]);
    }
  }, [user]);

  // Fetch favorites from the database
  const fetchFavorites = async () => {
    if (!user) return;
    
    try {
      // First get the favorite product IDs
      const { data: favoriteIds, error: favoritesError } = await supabase
        .from('favorites')
        .select('product_id')
        .eq('user_id', user.id);
      
      if (favoritesError) throw favoritesError;
      if (!favoriteIds || favoriteIds.length === 0) return;
      
      // Then get the full product details for each favorite
      const productIds = favoriteIds.map(f => f.product_id);
      
      const { data: products, error: productsError } = await supabase
        .from('products')
        .select('*')
        .in('id', productIds);
      
      if (productsError) throw productsError;
      if (!products) return;
      
      // Map products to favorite items
      const favoriteItems: FavoriteItem[] = products.map(product => ({
        productId: product.id,
        product: {
          id: product.id,
          name: product.name,
          description: product.description || '',
          image: product.image,
          price: product.price || 0,
          pointsPrice: product.points_price,
          brandId: product.brand_id || '',
          categoryId: product.category_id || '',
          subcategoryId: product.subcategory_id,
          stock: product.stock || 0,
          additionalImages: product.additional_images
        },
        dateAdded: new Date().toISOString() // We don't have the actual date from the DB
      }));
      
      setFavorites(favoriteItems);
    } catch (error) {
      console.error('Error loading favorites:', error);
      toast.error('Não foi possível carregar seus favoritos.');
    }
  };

  // Add a product to favorites
  const addToFavorites = async (item: FavoriteItem) => {
    if (!user) {
      toast.error('Você precisa estar logado para adicionar favoritos.');
      return;
    }
    
    try {
      const { error } = await supabase
        .from('favorites')
        .insert({ 
          user_id: user.id, 
          product_id: item.productId 
        });
        
      if (error) throw error;
      
      setFavorites(prev => [...prev, item]);
      toast.success(`${item.product.name} adicionado aos favoritos!`);
    } catch (error) {
      console.error('Error adding to favorites:', error);
      toast.error('Não foi possível adicionar aos favoritos.');
    }
  };

  // Remove a product from favorites
  const removeFromFavorites = async (productId: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('favorites')
        .delete()
        .eq('user_id', user.id)
        .eq('product_id', productId);
        
      if (error) throw error;
      
      setFavorites(prev => prev.filter(item => item.productId !== productId));
      toast.info('Item removido dos favoritos.');
    } catch (error) {
      console.error('Error removing from favorites:', error);
      toast.error('Não foi possível remover dos favoritos.');
    }
  };

  // Clear all favorites
  const clearFavorites = async () => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('favorites')
        .delete()
        .eq('user_id', user.id);
        
      if (error) throw error;
      
      setFavorites([]);
      toast.info('Todos os favoritos foram removidos.');
    } catch (error) {
      console.error('Error clearing favorites:', error);
      toast.error('Não foi possível limpar os favoritos.');
    }
  };

  // Check if a product is in favorites
  const isFavorite = (productId: string) => {
    return favorites.some(item => item.productId === productId);
  };

  return (
    <FavoritesContext.Provider
      value={{
        favorites,
        isFavorite,
        addToFavorites,
        removeFromFavorites,
        clearFavorites,
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
};

// Custom hook to use the favorites context
export const useFavorites = () => useContext(FavoritesContext);
